import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { SkillTemplate, Skill, ExternalSkill } from '../types';
import { fetchExternalSkills } from '../services/skillsApi';

interface UseExternalSkillsResult {
  skills: SkillTemplate[];
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  page: number;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  loadMore: () => void;
  retry: () => void;
}

function inferCategory(name: string): Skill['category'] {
  const lowerName = name.toLowerCase();

  if (/react|vue|css|ui|component|frontend|tailwind|button|modal|form|design|native/.test(lowerName)) {
    return 'frontend';
  }
  if (/api|server|express|database|backend|node|rest|graphql|prisma|supabase|auth/.test(lowerName)) {
    return 'backend';
  }
  if (/chart|viz|analytics|data|csv|excel|report|dashboard/.test(lowerName)) {
    return 'data';
  }
  if (/ai|ml|gpt|llm|openai|claude|langchain|embedding|vector|agent/.test(lowerName)) {
    return 'ai';
  }
  return 'integration';
}

function transformToSkillTemplate(skill: ExternalSkill): SkillTemplate {
  return {
    id: `ext-${skill.id}`,
    name: skill.name,
    description: `External skill from ${skill.topSource}`,
    category: inferCategory(skill.name),
    codeTemplate: skill.id,
    dependencies: [],
    source: 'external',
    installs: skill.installs,
    topSource: skill.topSource
  };
}

// Calculate search relevance score
function getSearchScore(skill: SkillTemplate, searchTerms: string[]): number {
  if (searchTerms.length === 0) return skill.installs || 0;

  const name = skill.name.toLowerCase();
  const topSource = (skill.topSource || '').toLowerCase();
  let score = 0;

  for (const term of searchTerms) {
    // Exact match in name - highest priority
    if (name === term) {
      score += 10000;
    }
    // Name starts with term
    else if (name.startsWith(term)) {
      score += 5000;
    }
    // Name contains term as whole word
    else if (name.split('-').includes(term) || name.split('_').includes(term)) {
      score += 3000;
    }
    // Name contains term
    else if (name.includes(term)) {
      score += 1000;
    }
    // Source contains term
    else if (topSource.includes(term)) {
      score += 500;
    }
  }

  // Add install count as secondary sort (normalized to prevent overwhelming relevance)
  score += Math.min(skill.installs || 0, 999) / 1000;

  return score;
}

// Filter and sort skills based on search
function filterAndSortSkills(skills: SkillTemplate[], searchTerm: string): SkillTemplate[] {
  if (!searchTerm.trim()) {
    // No search - return sorted by installs
    return [...skills].sort((a, b) => (b.installs || 0) - (a.installs || 0));
  }

  const searchTerms = searchTerm.toLowerCase().trim().split(/\s+/).filter(t => t.length > 0);

  // Filter skills that match any search term
  const filtered = skills.filter(skill => {
    const name = skill.name.toLowerCase();
    const topSource = (skill.topSource || '').toLowerCase();

    return searchTerms.some(term =>
      name.includes(term) || topSource.includes(term)
    );
  });

  // Sort by relevance score
  return filtered.sort((a, b) => {
    const scoreA = getSearchScore(a, searchTerms);
    const scoreB = getSearchScore(b, searchTerms);
    return scoreB - scoreA;
  });
}

export function useExternalSkills(): UseExternalSkillsResult {
  const [allSkills, setAllSkills] = useState<SkillTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');

  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Debounce search term for UI responsiveness
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 200);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [searchTerm]);

  // Client-side filtered and sorted skills
  const skills = useMemo(() => {
    return filterAndSortSkills(allSkills, debouncedSearch);
  }, [allSkills, debouncedSearch]);

  const loadSkills = useCallback(async (pageNum: number, append: boolean) => {
    setLoading(true);
    setError(null);

    try {
      // Always fetch without search param - skills.sh doesn't support search properly
      const response = await fetchExternalSkills(pageNum, '');
      const transformed = response.skills.map(transformToSkillTemplate);

      if (append) {
        setAllSkills(prev => {
          // Deduplicate skills
          const existingIds = new Set(prev.map(s => s.id));
          const newSkills = transformed.filter(s => !existingIds.has(s.id));
          return [...prev, ...newSkills];
        });
      } else {
        setAllSkills(transformed);
      }
      setHasMore(response.hasMore);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load external skills');
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    loadSkills(1, false);
  }, [loadSkills]);

  const loadMore = useCallback(() => {
    if (!loading && hasMore) {
      const nextPage = page + 1;
      setPage(nextPage);
      loadSkills(nextPage, true);
    }
  }, [loading, hasMore, page, loadSkills]);

  const retry = useCallback(() => {
    loadSkills(page, page > 1);
  }, [page, loadSkills]);

  // Show "has more" only if we haven't loaded all AND search results could benefit from more data
  const effectiveHasMore = hasMore && (debouncedSearch === '' || skills.length < 10);

  return {
    skills,
    loading,
    error,
    hasMore: effectiveHasMore,
    page,
    searchTerm,
    setSearchTerm,
    loadMore,
    retry
  };
}
